import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import HeaderStyles from './Header.module.css';
import AccountModal from './AccountModal';
import BadgesModal from './BadgesModal';
import SubmissionsModal from './SubmissionsModal';

const Header = () => {
  const navigate = useNavigate();
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [isGuest, setIsGuest] = useState(!localStorage.getItem('token'));
  const [username, setUsername] = useState('');
  const dropdownRef = useRef(null);
  const location = useLocation();
  const [isTeacher, setIsTeacher] = useState(false);
  const [isJinshiHolder, setIsJinshiHolder] = useState(false);


  useEffect(() => {
    const role = localStorage.getItem('role');
    setIsTeacher(role === 'teacher');
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUsername = localStorage.getItem('username');
  
    if (token && storedUsername) {
      setIsGuest(false);
      setUsername(storedUsername);
  
      // 🛠 Fetch user achievements dynamically
      fetch(`${process.env.REACT_APP_API_URL}/user/current-level`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then(response => response.json())
        .then(data => {
          if (data.achievements && data.achievements.jinshi) {
            setIsJinshiHolder(true); // ✅ Show Rate Submissions if Jinshi present
          } else {
            setIsJinshiHolder(false);
          }
        })
        .catch(error => {
          console.error('Error fetching current level:', error);
          setIsJinshiHolder(false); // Fail-safe
        });
    } else {
      setIsGuest(true);
      setUsername('');
      setIsJinshiHolder(false);
    }
  }, []);
  
  

  const handleProfileClick = () => {
    setProfileDropdown(!profileDropdown);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      window.location.reload();
      return;
    }
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/logout`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      if (response.ok) {
        console.log("Logout successful");
      } else {
        console.error("Logout failed");
      }
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      // Clear frontend state and redirect
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("role");
      sessionStorage.setItem("hasMadeSelection", "guest");
      setIsGuest(true);
      setProfileDropdown(false);
      setUsername("");
      navigate("/");
      window.location.reload();
    }
  };
  

  const handleLoginRedirect = () => {
    navigate('/');
  };

  const handleAccountClick = () => {
    setShowAccountModal(true);
    setProfileDropdown(false);
  };

  const handleBadgesClick = () => {
    setShowBadgesModal(true);
    setProfileDropdown(false);
  };

  const handleSubmissionsClick = () => {
    setShowSubmissionsModal(true);
    setProfileDropdown(false);
  };

  const closeModals = () => {
    setShowAccountModal(false);
    setShowBadgesModal(false);
    setShowSubmissionsModal(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <header className={HeaderStyles.header}>
        <nav className={HeaderStyles.navCenter}>
          <Link to="/" className={location.pathname === '/' ? HeaderStyles.active : ''}>Home</Link>
          {isTeacher ? (
            <Link to="/teacher/dashboard" className={location.pathname === '/teacher/dashboard' ? HeaderStyles.active : ''}>
              Dashboard
            </Link>
          ) : (
            <>
              <div className={HeaderStyles.dropdown}>
                <Link to="#about" className={location.pathname.startsWith('/about') ? HeaderStyles.active : ''}>About</Link>
                <div className={HeaderStyles.dropdownContent}>
                  <Link to="/about/website" className={location.pathname === '/about/website' ? HeaderStyles.active : ''}>
                    About Website
                  </Link>
                  <Link to="/about/jueju" className={location.pathname === '/about/jueju' ? HeaderStyles.active : ''}>
                    About Jueju
                  </Link>
                  <Link to="/about/english-jueju" className={location.pathname === '/about/english-jueju' ? HeaderStyles.active : ''}>
                    About English Jueju
                  </Link>
                </div>
              </div>
              <Link to="/learn/examination-path" className={location.pathname === '/learn/examination-path' ? HeaderStyles.active : ''}>
                Learn
              </Link>
              <Link
                to="/assess"
                className={location.pathname.startsWith('/assess') ? HeaderStyles.active : ''}
              >
                Assess
              </Link>
              {!isGuest && (
                isJinshiHolder ? (
                <Link
      		to="/rate-submissions"
      		className={location.pathname === '/rate-submissions' ? HeaderStyles.active : ''}
    		>
     		 Rate Submissions
    		</Link>
 		 ) : (
    		<Link
      		to="/rate-submissions"
      		className={location.pathname === '/rate-submissions' ? HeaderStyles.active : ''}
    		>
      		Jinshi Poetry Gallery
   		 </Link>
 		 )
		)}


              <div className={HeaderStyles.dropdown}>
                <Link to="/newmanprize" className={location.pathname === '/newmanprize' ? HeaderStyles.active : ''}>
                Newman Prize
                </Link>
                <div className={HeaderStyles.dropdownContent}>
                  <a
                    href="https://www.ou.edu/cis/research/institute-for-us-china-issues/us-china-cultural-issues/newman-prize-for-english-jueju"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more about the Newman Prize
                  </a>
                  <a href="https://youtu.be/BEzwJ6AtUZ8" target="_blank" rel="noopener noreferrer">
                    2024–2025 Winners
                  </a>
                  <a href="https://www.youtube.com/watch?v=4tV5PT1f0eE&t=601s" target="_blank" rel="noopener noreferrer">
                    2022–2023 Winners
                  </a>
                  <a href="https://www.youtube.com/watch?v=3Nzaq6fPvz4&t=263s" target="_blank" rel="noopener noreferrer">
                    2020–2021 Winners
                  </a>
                  <a href="https://www.youtube.com/watch?v=3Nzaq6fPvz4&t=263s" target="_blank" rel="noopener noreferrer">
                    2018–2019 Winners
                  </a>
                  <a href="https://www.youtube.com/watch?v=ArHz0o-et9I" target="_blank" rel="noopener noreferrer">
                    2016–2017 Winners
                  </a>
                  <a href="https://www.youtube.com/watch?v=pfByqTgSWxs&t=5s" target="_blank" rel="noopener noreferrer">
                    2014–2015 Winners
                  </a>
                  <a href="https://www.youtube.com/watch?v=et8GpdQVQx0" target="_blank" rel="noopener noreferrer">
                    2012–2013 Winners
                  </a>
                </div>
              </div>
            </>
          )}
        </nav>
        <div className={HeaderStyles.profileSection} onClick={handleProfileClick} ref={dropdownRef}>
          <span className={HeaderStyles.profileIcon}>👤</span>
          <span className={HeaderStyles.profileStatus}>
            {isGuest ? 'Guest Mode' : `${username}`}
          </span>
          <span className={HeaderStyles.dropdownArrow}>▼</span>
          {profileDropdown && (
            <div className={HeaderStyles.profileDropdown}>
              {isGuest ? (
                <button onClick={handleLoginRedirect}>Login</button>
              ) : (
                <>
                  <button onClick={handleAccountClick}>Account</button>
                  {!isTeacher && <button onClick={handleBadgesClick}>Badges</button>}
                  {!isTeacher && <button onClick={handleSubmissionsClick}>Submissions</button>}
                  <button onClick={handleLogout}>Logout</button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Modals */}
      {showAccountModal && <AccountModal closeModal={closeModals} />}
      {showBadgesModal && <BadgesModal closeModal={closeModals} />}
      {showSubmissionsModal && <SubmissionsModal closeModal={closeModals} />}
    </>
  );
};

export default Header;


